<script lang="ts">
  import DeliveredOn from '$components/delivered-on.svelte';
  import KlarnaPlacement from '$components/klarna-placement.svelte';
  import Button from '$components/ui/button.svelte';

  import { cn } from '$lib/utils';
  import { t } from '$lib/i18n';

  import type { GetProductQuery, MoneyFragment } from '$gql/storefront/operations';
  import NotifyMe from '$components/notify-me.svelte';
  import { addToCart } from '$lib/cart';
  import { optimalUSCountry } from '$lib/country';
  import { cart, getProductCategory, lastAddedItemLocation } from '$lib/cart/cart';
  import { AddToCartLocations, elevarAnalyticsEvent, ElevarAnalyticsEvents } from '$lib/analytics';
  import Icon from '$components/ui/icon/icon.svelte';
  import type { CurrencyCode } from '$gql/storefront/schemas';
  import { formatCurrency } from '$lib/utils/currency';
  import {
    categoryCampaigns,
    getCampaignColors,
    freeGiftCampaign,
    tieredCampaignByProduct
  } from '$lib/stores/campaigns';
  import {
    DEFAULT_TITLE,
    DEFAULT_VENDOR,
    GUEST,
    PRE_ORDER_ATTRIBUTE,
    ProductMetafields,
    SHOPIFY_STORE_NAME
  } from '$lib/constants';
  import { trimId, trimVariantId } from '$lib/utils/shopify/productsId';
  import { buildPath, getFullURL } from '$lib/utils/paths';
  import { region } from '$lib/region';
  import { lang } from '$lib/lang';
  import { user } from '$lib/stores/user';
  import { getAnchorFromCmsLink } from '$lib/utils/cms';
  import { format } from 'date-fns';

  const styles = {
    price: 'whitespace-nowrap leading-none font-medium'
  } as const;

  export let product: NonNullable<GetProductQuery['product']>;
  export let selectedProductVariant:
    | NonNullable<GetProductQuery['product']>['variants']['nodes'][number]
    | undefined;
  export let isPreSale: boolean;
  let className: string | undefined = undefined;
  export { className as class };

  export let currency: CurrencyCode;
  export let compareAtPrice: MoneyFragment | null | undefined;
  export let showJustAddCardButton: boolean | undefined = false;
  export let selectedPlanId: string | undefined = undefined;
  export let hideKlarna: boolean | undefined = undefined;
  export let isFreeGift: boolean | undefined = undefined;

  let showAdded = false;

  $: selectedPlanAllocation = selectedProductVariant?.sellingPlanAllocations.edges.find(
    (allocation) => allocation.node.sellingPlan.id === selectedPlanId
  )?.node;

  $: selectedPlanAllocationPriceAdjustment = selectedPlanAllocation?.priceAdjustments[0];

  $: price = selectedPlanAllocation
    ? +selectedPlanAllocationPriceAdjustment?.price.amount
    : +selectedProductVariant?.price?.amount || product.priceRange?.minVariantPrice.amount;

  $: tieredCampaign = product ? tieredCampaignByProduct(product) : undefined;
  $: tieredCampaignColors = tieredCampaign
    ? getCampaignColors(tieredCampaign.campaign_color)
    : undefined;

  $: variantAvailableForSale = selectedProductVariant?.availableForSale;
  $: onSale = !!(compareAtPrice?.amount && +compareAtPrice.amount > +price);
  $: hasTieredCampaign = !!tieredCampaign && !tieredCampaign.isProductExcluded(product);
  $: hasCategoryCampaign =
    !!$categoryCampaigns &&
    $categoryCampaigns.some((campaign) => !campaign.isProductExcluded(product)) &&
    !hasTieredCampaign;
  $: categoryCampaign = $categoryCampaigns?.find(
    (campaign) => !campaign.isProductExcluded(product)
  );

  $: tieredAltTextColor = tieredCampaignColors?.altTextColor;
  $: tieredAltBackgroundColor = tieredCampaignColors?.altBackgroundColor;
  $: isBlackTieredColor = tieredCampaignColors?.isBlackColor;

  $: freeGiftCampaignColors = $freeGiftCampaign?.campaign_color
    ? getCampaignColors($freeGiftCampaign?.campaign_color)
    : null;

  $: freeGiftAltTextColor = freeGiftCampaignColors?.altTextColor;
  $: freeGiftAltBackgroundColor = freeGiftCampaignColors?.altBackgroundColor;
  $: isBlackFreeGiftColor = freeGiftCampaignColors?.isBlackColor;

  let isBlackCategoryColor: boolean;
  let categoryAltTextColor: string | undefined = undefined;
  let categoryAltBackgroundColor: string | undefined = undefined;

  $: if (categoryCampaign?.campaign_color) {
    const categoryCampaignColors = getCampaignColors(categoryCampaign.campaign_color);
    isBlackCategoryColor = categoryCampaignColors.isBlackColor;
    categoryAltTextColor = categoryCampaignColors.altTextColor;
    categoryAltBackgroundColor = categoryCampaignColors.altBackgroundColor;
  }

  let shopPayUrl: string;
  $: (async () => {
    if (selectedProductVariant?.id) {
      const url = `https://${SHOPIFY_STORE_NAME[$region]}.myshopify.com/cart/${trimVariantId(selectedProductVariant.id)}:1?payment=shop_pay`;
      const res = await fetch(
        $buildPath({
          path: `/get-checkout-url?checkout_url=${encodeURIComponent(url)}`
        })
      );

      shopPayUrl = res.ok ? await res.text() : url;
    }
  })();

  $: cartItems = $cart?.lines.nodes.filter((line) => line.quantity > 0) ?? [];

  let loading = false;

  const triggerElevar = () => {
    if (!selectedProductVariant) return;

    let collectionUrl = `/collections/${getProductCategory(product.title, product.productType) ?? ''}`;
    const variantId = trimVariantId(selectedProductVariant.id);
    const variantTitle =
      selectedProductVariant.title === DEFAULT_TITLE || selectedProductVariant.title === 'Title'
        ? product.title
        : selectedProductVariant.title;

    const newPrice = compareAtPrice?.amount || price;
    const productPrice = hasCategoryCampaign
      ? categoryCampaign?.getDiscountedPrice(+price)
      : newPrice;

    const currentCartLine = cartItems.find(
      (line) => line.merchandise.id === selectedProductVariant.id
    );
    const productQuantity = (currentCartLine?.quantity ?? 0) + 1;

    elevarAnalyticsEvent({
      event: ElevarAnalyticsEvents.ADD_TO_CART,
      user_properties: {
        visitor_type: GUEST
      },
      ecommerce: {
        currencyCode: selectedProductVariant.price.currencyCode,
        add: {
          actionField: {
            list: collectionUrl
          },
          products: [
            {
              id: selectedProductVariant?.sku,
              name: product.title,
              brand: product.vendor || DEFAULT_VENDOR,
              category: product?.productType as string,
              variant: variantTitle,
              price: `${productPrice}`,
              list: collectionUrl,
              product_id: trimId(product.id),
              quantity: `${productQuantity}`,
              variant_id: variantId,
              compare_at_price: compareAtPrice?.amount || '0.0',
              image: (product.images?.nodes || [])[0]?.url || '',
              url: getFullURL({
                path: `/products/${product.handle}`,
                region: $region,
                lang: $lang,
                searchQuery: `variant=${variantId}`
              })
            }
          ]
        }
      }
    });
  };

  const handleAddToCart = () => {
    if (!selectedProductVariant) return;

    lastAddedItemLocation.set(AddToCartLocations.PDP_BUTTON);

    showAdded = true;
    setTimeout(() => (showAdded = false), 500);

    $addToCart([
      {
        merchandiseId: selectedProductVariant.id,
        quantity: 1,
        sellingPlanId: selectedPlanId,
        attributes: isPreSale
          ? [{ key: PRE_ORDER_ATTRIBUTE, value: preSaleDayFormatted ?? '' }]
          : []
      }
    ]);

    triggerElevar();
  };

  $: preSaleDay = product.metafields.find((p) => p?.key === ProductMetafields.PRE_ORDER_DAY)?.value;
  $: preSaleDayFormatted = preSaleDay ? format(new Date(preSaleDay), 'MMMM do') : undefined;
</script>

<div
  class={cn(
    'bg-background pb-2 px-0 pt-3 lg:pb-0 flex flex-col',
    showJustAddCardButton && 'bg-transparent',
    className
  )}
  style:--tieredAltBackgroundColor={tieredAltBackgroundColor}
  style:--tieredAltTextColor={tieredAltTextColor}
  style:--categoryAltBackgroundColor={categoryAltBackgroundColor}
  style:--categoryAltTextColor={categoryAltTextColor}
  style:--freeGiftAltBackgroundColor={freeGiftAltBackgroundColor}
  style:--freeGiftAltTextColor={freeGiftAltTextColor}
>
  {#if variantAvailableForSale || isPreSale}
    {#if !showJustAddCardButton && !isFreeGift}
      <p class="text-sm mb-6 text-primary/60 flex items-center gap-x-1">
        <Icon icon="box" size="md" />
        <DeliveredOn
          start={preSaleDay}
          customMessage={categoryCampaign?.pdp_custom_delivery_message}
        />
      </p>
    {/if}

    {#if !showJustAddCardButton}
      <div class="mb-4 flex items-center gap-2">
        <p
          class={cn(
            styles.price,
            hasCategoryCampaign && 'text-[var(--categoryAltTextColor)]',
            hasCategoryCampaign && isBlackCategoryColor && 'text-accent',
            isFreeGift && 'line-through text-primary/40'
          )}
        >
          {$formatCurrency(
            currency,
            hasCategoryCampaign && categoryCampaign
              ? categoryCampaign?.getDiscountedPrice(+price)
              : +price
          )}
        </p>
        {#if onSale && compareAtPrice && !hasCategoryCampaign && !isFreeGift}
          <p class={cn(styles.price, 'text-primary/40 line-through')}>
            {$formatCurrency(currency, +compareAtPrice.amount)}
          </p>
        {/if}
        {#if hasCategoryCampaign && categoryCampaign && !isFreeGift}
          <p class={cn(styles.price, 'text-primary/40 line-through')}>
            {$formatCurrency(currency, +price)}
          </p>
        {/if}

        {#if hasTieredCampaign && tieredCampaign && !isFreeGift}
          <p
            class={cn(
              'text-sm px-1.5 h-5 flex items-center rounded-[6px] text-[var(--tieredAltTextColor)] bg-[var(--tieredAltBackgroundColor)]',
              isBlackTieredColor && 'text-highlight bg-accent'
            )}
          >
            {$t('product-card.get-for', {
              price: $formatCurrency(currency, tieredCampaign.getDiscountedPrice(+price)),
              discount: tieredCampaign.discount
            })}
          </p>
        {/if}

        {#if isFreeGift}
          <p
            class={cn(
              'text-sm px-1.5 h-5 flex items-center rounded-[6px] text-[var(--freeGiftAltTextColor)] bg-[var(--freeGiftAltBackgroundColor)]',
              isBlackFreeGiftColor && 'text-highlight bg-accent'
            )}
          >
            {$t('free-gift.gift-with-purchase')}
          </p>
        {/if}
      </div>
    {/if}

    {#if isFreeGift && $freeGiftCampaign}
      <Button
        size="sm"
        as="a"
        href={$getAnchorFromCmsLink($freeGiftCampaign.free_gift_pdp_button_link).href}
        >{$freeGiftCampaign.free_gift_pdp_button_text}</Button
      >
    {:else}
      <div class="flex gap-2">
        {#if $region !== 'eu' && $user}
          <Button
            name={$t('cart.shop-pay')}
            data-sveltekit-reload
            as="a"
            href={shopPayUrl}
            variant="primary"
            class="bg-white hover:bg-white/80 active:bg-white/60 flex-1"
          >
            <Icon icon="shop" class="w-auto h-auto" />
          </Button>
        {/if}
        <Button
          name="add-to-cart"
          on:click={handleAddToCart}
          {loading}
          disabled={!variantAvailableForSale && !isPreSale}
          class="flex-1"
          size="sm"
          >{isPreSale
            ? $t('pdp.pre-sale')
            : showAdded
              ? $t('products.added')
              : $t('products.add-to-cart')}
        </Button>
      </div>
    {/if}
    <!-- Klarna has some issues with multicurrency so we're hiding the banner of non US regions of the US store
    https://docs.klarna.com/platform-solutions/shopify/klarna-payments-for-shopify/known-constraints/ -->
    {#if $optimalUSCountry !== 'CA' && $optimalUSCountry !== 'AU' && !showJustAddCardButton && !hideKlarna && !isFreeGift && !isPreSale}
      <KlarnaPlacement purchaseAmount={price * 100} />
    {/if}
  {:else if selectedProductVariant && !showJustAddCardButton && !isFreeGift}
    {#if !showJustAddCardButton}
      <div class="mb-4 flex items-center gap-2">
        <p class={cn(styles.price, hasCategoryCampaign && 'text-accent')}>
          {$formatCurrency(
            currency,
            hasCategoryCampaign && categoryCampaign
              ? categoryCampaign?.getDiscountedPrice(+price)
              : +price
          )}
        </p>
        {#if onSale && compareAtPrice && !hasCategoryCampaign}
          <p class={cn(styles.price, 'text-primary/40 line-through')}>
            {$formatCurrency(currency, +compareAtPrice.amount)}
          </p>
        {/if}
        {#if hasCategoryCampaign && categoryCampaign}
          <p class={cn(styles.price, 'text-primary/40 line-through')}>
            {$formatCurrency(currency, +price)}
          </p>
        {/if}

        {#if hasTieredCampaign && tieredCampaign}
          <p
            class={cn(
              'text-sm px-1.5 h-5 flex items-center rounded-[6px] text-[var(--tieredAltTextColor)] bg-[var(--tieredAltBackgroundColor)]',
              isBlackTieredColor && 'text-highlight bg-accent'
            )}
          >
            {$t('product-card.get-for', {
              price: $formatCurrency(currency, tieredCampaign.getDiscountedPrice(+price)),
              discount: tieredCampaign.discount
            })}
          </p>
        {/if}
      </div>
    {/if}

    <p class="text-lg font-semibold">{$t('pdp.out-of-stock')}</p>
    <p class="text-md mt-0.5">{$t('pdp.out-of-stock.description')}</p>
    <NotifyMe variant={selectedProductVariant.id} productId={product.id} />
  {/if}
</div>
